import type { CSSProperties, ReactNode } from "react";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

/**
 * A vertical list whose first `count` items reorder by drag (pointer or
 * keyboard), with the same dnd-kit setup as the builder's question list. Items
 * are addressed by index, as the editors that host it address them: a drop
 * reports `onMove(from, to)` and the host writes the reordered array through
 * its own writer. Items at `count` and after are not sortable: nothing drags
 * them and nothing drops among them. Below two sortable items the list is
 * rendered as it is, with no grips. It renders dnd-kit's two hidden
 * accessibility `<div>`s where it is placed, so a host puts it outside a
 * table's sections (around the `<table>`, never inside `<tbody>`).
 *
 * Host-agnostic: every string comes from props.
 */
export function SortableList(props: {
  idPrefix: string;
  count: number;
  onMove: (from: number, to: number) => void;
  children: ReactNode;
}) {
  const { idPrefix, count, onMove, children } = props;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  if (count < 2) return <>{children}</>;
  const ids = Array.from({ length: count }, (_, index) =>
    sortableItemId(idPrefix, index),
  );
  const handleDragEnd = (event: DragEndEvent) => {
    if (!event.over) return;
    const from = ids.indexOf(String(event.active.id));
    const to = ids.indexOf(String(event.over.id));
    if (from === -1 || to === -1 || from === to) return;
    onMove(from, to);
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

function sortableItemId(idPrefix: string, index: number): string {
  return `${idPrefix}-${index}`;
}

/**
 * Item `index` of a `SortableList` of `count` sortable items. `handle` is its
 * drag grip when it is sortable, else null; the host renders it where its row
 * starts and puts `setNodeRef` and `style` on the element that moves.
 */
export function SortableItem(props: {
  idPrefix: string;
  index: number;
  count: number;
  handleLabel: string;
  handleTestId?: string;
  children: (item: {
    setNodeRef: (element: HTMLElement | null) => void;
    style: CSSProperties | undefined;
    handle: ReactNode;
  }) => ReactNode;
}) {
  const { idPrefix, index, count, handleLabel, handleTestId, children } = props;
  const sortable = count >= 2 && index < count;
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableItemId(idPrefix, index), disabled: !sortable });
  if (!sortable) {
    return (
      <>
        {children({ setNodeRef: () => undefined, style: undefined, handle: null })}
      </>
    );
  }
  return (
    <>
      {children({
        setNodeRef,
        style: {
          transform: CSS.Transform.toString(transform),
          transition,
          ...(isDragging ? { position: "relative", zIndex: 1 } : {}),
        },
        handle: (
          <button
            type="button"
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            aria-label={handleLabel}
            title={handleLabel}
            data-testid={handleTestId}
            className="flex h-[20px] w-[14px] shrink-0 touch-none cursor-grab items-center justify-center rounded-[4px] text-muted-foreground/30 transition-colors hover:text-muted-foreground focus-visible:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-highlight active:cursor-grabbing"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        ),
      })}
    </>
  );
}
